import { Alert, Drawer } from '@mui/material';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { Stack } from '@mui/system';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';
import { Link, Outlet, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { useGetBeneficiaryQuery, useGetTransactionsQuery } from '@/api/v1';
import { FullScreenLoader } from '@/components/molecules/FullScreenLoader';
import { ActivitySearchBar } from '@/components/organisms/ActivitySearchBar';
import { MainScreen } from '@/components/organisms/MainScreen';
import { TransactionsTable } from '@/components/organisms/TransactionsTable';
import { FF_DASHBOARD_NEW_INTERNAL_TRANSFERS } from '@/data/featureFlags';
import { useCustomer } from '@/hooks/useCustomer';
import { useFeatureFlag } from '@/hooks/useFeatureFlags';
import { useTranslator } from '@/hooks/useTranslator';
import { useTypedSearchParams } from '@/hooks/useTypedSearchParams';
import { CustomerBalanceSection } from '@/Screens/Dashboard/Customers/CustomerBalanceSection';
import { NoCustomerActivityResultsView } from '@/Screens/Dashboard/Customers/NoCustomerActivityResultsView';
import { NoCustomerActivityView } from '@/Screens/Dashboard/Customers/NoCustomerActivityView';
import {
  ConvertLocationState,
  InternalTransferLocationState,
} from '@/Screens/Dashboard/Flows/locationState';
import { BeneficiaryType } from '@/types/customer';
import { VerificationResult } from '@/types/customerKYXVerification';
import { GetTransactionsFilters } from '@/types/getTransactionsFilters';
import { CUSTOMER_ACTIVITY_COLUMNS } from '@/types/transactionColumn';
import { CUSTOMER_ACTIVITY_FILTERS } from '@/types/transactionFilterField';
import { countTransactionFilters } from '@/utility/countTransactionFilters';

export const BeneficiaryDetails: React.FC = (): React.ReactElement => {
  const { beneficiaryId = '', transactionId = '' } = useParams<{
    readonly beneficiaryId: string;
    readonly transactionId: string;
  }>();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const tr = useTranslator();
  const customer = useCustomer();
  const isInternalTransfersEnabled = useFeatureFlag(FF_DASHBOARD_NEW_INTERNAL_TRANSFERS);
  const [filters, setFilters] = useTypedSearchParams<GetTransactionsFilters>(
    GetTransactionsFilters.initial(),
  );

  const {
    data: beneficiary,
    isLoading,
    isError,
  } = useGetBeneficiaryQuery(beneficiaryId, { skip: !beneficiaryId });

  const title = tr('ACCOUNTS');

  const onRowClick = React.useCallback(
    (txId: string): void => {
      navigate({
        pathname: `activity/${txId}`,
        search: search.toString(),
      });
    },
    [navigate, search],
  );

  const closeTransactionDetails = React.useCallback((): void => {
    navigate({
      pathname: '.',
      search: search.toString(),
    });
  }, [navigate, search]);

  const handleFiltersChange = React.useCallback(
    (newFilters: GetTransactionsFilters): void => {
      const { walletCurrency } = filters as GetTransactionsFilters & {
        readonly walletCurrency?: string;
      };
      setFilters({
        ...newFilters,
        ...(walletCurrency ? { walletCurrency } : {}),
      } as GetTransactionsFilters);
    },
    [filters, setFilters],
  );

  const filtersCount = React.useMemo((): number => countTransactionFilters(filters), [filters]);

  const queryFilters = React.useMemo(
    (): Partial<GetTransactionsFilters> => ({
      ...filters,
      beneficiaryId: beneficiaryId || undefined,
    }),
    [filters, beneficiaryId],
  );

  const { data: transactionsPage } = useGetTransactionsQuery(queryFilters, {
    skip: !beneficiaryId,
  });

  const hasNoTransactions = !transactionsPage || transactionsPage.content.length === 0;
  const hasNoContent = hasNoTransactions && filtersCount === 0;

  if (isLoading) {
    return <FullScreenLoader loading />;
  }

  if (isError || !beneficiary) {
    return (
      <MainScreen title={title}>
        <Alert severity="error">{tr('ERROR_LOADING_CUSTOMER')}</Alert>
      </MainScreen>
    );
  }

  const beneficiaryName: string =
    beneficiary.type === BeneficiaryType.business
      ? (beneficiary.business?.name ?? '')
      : `${beneficiary.person?.firstName ?? ''} ${beneficiary.person?.lastName ?? ''}`.trim();

  const breadcrumbs: React.ReactElement = (
    <Stack direction="row" alignItems="center" gap={1}>
      <Typography
        component={Link}
        to="/accounts"
        variant="h6"
        color="text.secondary"
        sx={styles.title}>
        {title}
      </Typography>
      <Typography variant="h6" color="text.secondary">
        {'>'}
      </Typography>
      {beneficiaryName && <Typography variant="h6">{beneficiaryName}</Typography>}
    </Stack>
  );

  return (
    <MainScreen title={breadcrumbs}>
      <Stack gap={4} sx={styles.container}>
        <CustomerBalanceSection
          beneficiaryId={beneficiaryId}
          beneficiaryType={beneficiary.type}
          beneficiaryName={beneficiaryName || undefined}
          transferLink={`/accounts/${beneficiaryId}/transfer`}
          depositLink={`/accounts/${beneficiaryId}/deposit`}
          internalTransferLink={isInternalTransfersEnabled ? '/home/internal-transfer' : undefined}
          internalTransferLinkState={
            isInternalTransfersEnabled && beneficiaryId !== customer.beneficiaryId
              ? ({
                  presetCustomerBeneficiaryId: beneficiaryId,
                  presetDirection: 'main_to_customer',
                } satisfies InternalTransferLocationState)
              : undefined
          }
          convertLink="/home/convert"
          convertLinkState={
            {
              presetBeneficiaryId: beneficiaryId,
            } satisfies ConvertLocationState
          }
        />

        <Paper elevation={3} sx={styles.activityPaper}>
          <Stack gap={4}>
            <Typography variant="h6">{tr('ACTIVITY')}</Typography>
            {!hasNoContent && (
              <ActivitySearchBar
                filtersCount={filtersCount}
                filters={filters}
                onFiltersChange={handleFiltersChange}
                beneficiaryId={beneficiaryId}
                visibleFilters={CUSTOMER_ACTIVITY_FILTERS}
                isDownloadDisabled={hasNoTransactions}
              />
            )}
            <TransactionsTable
              beneficiaryId={beneficiaryId}
              filters={filters}
              onFiltersChange={handleFiltersChange}
              onRowClick={onRowClick}
              noContentView={
                <NoCustomerActivityView
                  verificationResult={VerificationResult.APPROVED}
                  beneficiaryId={beneficiaryId}
                />
              }
              noResultsView={<NoCustomerActivityResultsView />}
              visibleColumns={CUSTOMER_ACTIVITY_COLUMNS}
            />
          </Stack>
        </Paper>
      </Stack>

      {transactionId ? (
        <Drawer open anchor="right" onClose={closeTransactionDetails}>
          <Outlet />
        </Drawer>
      ) : (
        <Outlet />
      )}
    </MainScreen>
  );
};

const styles: Record<string, SystemStyleObject> = {
  title: {
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  container: {
    pb: 2,
  },
  activityPaper: {
    px: 4,
    py: 3,
    borderRadius: 2,
  },
};
